import { useMemo } from 'react'

const MAP_SIZE = 256
const REFRACTIVE_INDEX = 1.5
const BEZEL_RATIO = 0.18
const SAMPLES = 127
const FILTER_ID = 'landing-liquid-glass'
const DISPLACEMENT_GAIN = 14

type MapBundle = {
  displacementMapDataUrl: string
  maximumDisplacement: number
  specularMapDataUrl: string
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1)
  return t * t * (3 - 2 * t)
}

// Convex squircle profile used in the article.
function convexSquircle(distanceFromSide: number): number {
  const x = clamp(distanceFromSide, 0, 1)
  const inner = 1 - Math.pow(1 - x, 4)
  return inner <= 0 ? 0 : Math.pow(inner, 0.25)
}

function derivativeAt(distanceFromSide: number): number {
  const delta = 0.001
  const x1 = clamp(distanceFromSide - delta, 0, 1)
  const x2 = clamp(distanceFromSide + delta, 0, 1)
  const y1 = convexSquircle(x1)
  const y2 = convexSquircle(x2)
  return (y2 - y1) / Math.max(x2 - x1, 1e-6)
}

function toPngDataUrl(size: number, pixels: Uint8ClampedArray): string {
  if (typeof ImageData === 'undefined') {
    return ''
  }

  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  if (!context) {
    return ''
  }

  try {
    const rgbaPixels = new Uint8ClampedArray(pixels)
    const imageData = new ImageData(rgbaPixels, size, size)
    context.putImageData(imageData, 0, 0)
    return canvas.toDataURL('image/png')
  } catch {
    return ''
  }
}

function buildLiquidGlassMaps(): MapBundle {
  const displacementMagnitudes = new Array<number>(SAMPLES + 1).fill(0)
  let maximumDisplacement = 0

  // First-principles pre-calculation on a single radius (as in the guide).
  for (let i = 0; i <= SAMPLES; i += 1) {
    const distanceFromSide = i / SAMPLES
    const derivative = Math.abs(derivativeAt(distanceFromSide))
    const thetaIncident = Math.atan(derivative)
    const thetaRefracted = Math.asin(clamp(Math.sin(thetaIncident) / REFRACTIVE_INDEX, -1, 1))
    const bend = Math.max(0, thetaIncident - thetaRefracted)
    const displacement = Math.tan(bend) * DISPLACEMENT_GAIN
    displacementMagnitudes[i] = displacement
    if (displacement > maximumDisplacement) {
      maximumDisplacement = displacement
    }
  }

  if (maximumDisplacement <= 0) {
    maximumDisplacement = 12
  }

  const displacementPixels = new Uint8ClampedArray(MAP_SIZE * MAP_SIZE * 4)
  const specularPixels = new Uint8ClampedArray(MAP_SIZE * MAP_SIZE * 4)

  const maxRadius = 0.5
  const bezelWidth = maxRadius * BEZEL_RATIO
  const lightX = -0.62
  const lightY = -0.78

  for (let y = 0; y < MAP_SIZE; y += 1) {
    for (let x = 0; x < MAP_SIZE; x += 1) {
      const index = (y * MAP_SIZE + x) * 4
      const u = (x + 0.5) / MAP_SIZE
      const v = (y + 0.5) / MAP_SIZE
      const dx = u - 0.5
      const dy = v - 0.5
      const radius = Math.sqrt(dx * dx + dy * dy)

      // Outside the lens shape: neutral displacement and transparent highlight.
      if (radius > maxRadius) {
        displacementPixels[index] = 128
        displacementPixels[index + 1] = 128
        displacementPixels[index + 2] = 128
        displacementPixels[index + 3] = 255

        specularPixels[index] = 255
        specularPixels[index + 1] = 255
        specularPixels[index + 2] = 255
        specularPixels[index + 3] = 0
        continue
      }

      const distanceToBorder = maxRadius - radius
      const bezelDistance = clamp(distanceToBorder / bezelWidth, 0, 1)
      const lookupIndex = Math.round(bezelDistance * SAMPLES)
      const normalizedMagnitude = displacementMagnitudes[lookupIndex] / maximumDisplacement

      // Strongest refraction near edge, fading toward center.
      const edgeWeight = 1 - smoothstep(0.08, 1, bezelDistance)
      const magnitude = normalizedMagnitude * edgeWeight
      const angle = Math.atan2(-dy, -dx)
      const vectorX = Math.cos(angle) * magnitude
      const vectorY = Math.sin(angle) * magnitude

      displacementPixels[index] = clamp(Math.round(128 + vectorX * 127), 0, 255)
      displacementPixels[index + 1] = clamp(Math.round(128 + vectorY * 127), 0, 255)
      displacementPixels[index + 2] = 128
      displacementPixels[index + 3] = 255

      // Rim-light specular map blended on top of refraction.
      const nx = radius > 0 ? dx / radius : 0
      const ny = radius > 0 ? dy / radius : 0
      const lightDot = clamp(nx * lightX + ny * lightY, 0, 1)
      const edgeMask = 1 - smoothstep(0.04, 0.26, distanceToBorder / maxRadius)
      const alpha = clamp(Math.round(255 * edgeMask * (0.14 + lightDot * 0.56)), 0, 255)

      specularPixels[index] = 255
      specularPixels[index + 1] = 255
      specularPixels[index + 2] = 255
      specularPixels[index + 3] = alpha
    }
  }

  return {
    displacementMapDataUrl: toPngDataUrl(MAP_SIZE, displacementPixels),
    maximumDisplacement,
    specularMapDataUrl: toPngDataUrl(MAP_SIZE, specularPixels),
  }
}

export default function LiquidGlassFilterDefs() {
  const maps = useMemo<MapBundle | null>(() => {
    if (typeof document === 'undefined') {
      return null
    }
    return buildLiquidGlassMaps()
  }, [])

  if (!maps?.displacementMapDataUrl || !maps.specularMapDataUrl) {
    return null
  }

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      className="liquid-glass-svg-defs"
      width="0"
      height="0"
      xmlns="http://www.w3.org/2000/svg"
      style={{ position: 'fixed', inset: 0, pointerEvents: 'none' }}
    >
      <defs>
        <filter
          id={FILTER_ID}
          colorInterpolationFilters="sRGB"
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
        >
          <feImage
            href={maps.displacementMapDataUrl}
            x="0"
            y="0"
            width="100%"
            height="100%"
            preserveAspectRatio="none"
            result="displacement_map"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="displacement_map"
            scale={maps.maximumDisplacement}
            xChannelSelector="R"
            yChannelSelector="G"
            result="refracted"
          />
          <feImage
            href={maps.specularMapDataUrl}
            x="0"
            y="0"
            width="100%"
            height="100%"
            preserveAspectRatio="none"
            result="specular_map"
          />
          <feBlend in="refracted" in2="specular_map" mode="screen" />
        </filter>
      </defs>
    </svg>
  )
}
